import {
  Cluster,
  ClusterStats,
  Marker,
  Renderer,
} from "@googlemaps/markerclusterer";
import { DeviceEvent, DeviceListingResponse } from "../api/types/Camera";
import { APP_CONFIG } from "../constants/constants";
import { TDataPoint } from "../pages/map-fleet/type";
import { ColorRGB } from "../types/common";
import { TLatLng } from "../types/map";
import { removeScript } from "./common";
import { consistentDisplayAssetItem } from "./vehicle";
import { toast } from "react-toastify";

type TMapGetCurrentPositionCallback = (position: TLatLng) => void;

export const mapGetCurrentPosition = (
  callback: TMapGetCurrentPositionCallback
) => {
  if (navigator.geolocation) {
    const errorCallback = (error: GeolocationPositionError) => {
      if (APP_CONFIG.DEBUG.MAPS) {
        console.error("!!! GEOLOCATION ERROR !!!:", error.message);
        console.log("Full error object:", error);
      }
      toast.error("Could not get your location ");
    };
    navigator.geolocation.getCurrentPosition((position) => {
      if (APP_CONFIG.DEBUG.MAPS)
        console.log(
          "[mapGetCurrentPosition] Retrieved user current geo position",
          `${position.coords.latitude}, ${position.coords.longitude}`
        );
      callback({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });
    }, errorCallback);
  } else {
    if (APP_CONFIG.DEBUG.MAPS)
      console.log("Geolocation is not supported by this browser.");
  }
};

export const removeBingMap = () => {
  if (APP_CONFIG.DEBUG.MAPS) console.log("removeBingMap");
  removeScript(APP_CONFIG.MAPS.SCRIPT_ID);
};

// --------------------------------------
// Google Maps operations for map entites
// --------------------------------------

// get the distance between two locations
export const getDistanceFromCenter = (
  loc1: google.maps.LatLng,
  loc2: google.maps.LatLng
) => {
  // const Microsoft = (window as any).Microsoft;
  // if (APP_CONFIG.DEBUG.MAPS)
  //   console.log(
  //     "[getDistanceFromCenter]",
  //     loc1.getLocation().latitude,
  //     loc2.getLocation().longitude
  //   );
  // return Microsoft.Maps.SpatialMath.getDistanceTo(
  //   loc1.getLocation(),
  //   loc2.getLocation(),
  //   APP_CONFIG.MAPS.DB_GEOZONE_RADIUS_UNIT_FOR_MAPS()
  // );
  const distance = google.maps.geometry.spherical.computeDistanceBetween(
    loc1,
    loc2
  );
  return distance;
};

export const getDistanceBetweenTwoLocations = (
  loc1: TLatLng,
  loc2: TLatLng
) => {
  const google = (window as any).google;

  const from = new google.maps.LatLng(loc1.latitude, loc1.longitude);
  const to = new google.maps.LatLng(loc2.latitude, loc2.longitude);

  return google.maps.geometry.spherical.computeDistanceBetween(from, to);
};

// calculate the locations for a regular polygon that has 36 locations which will result in an approximate circle.
export const getCircleLocs = (
  center: google.maps.LatLng,
  radius: any,
  edges: number,
  isPolygon: boolean = false
) => {
  // const Microsoft = (window as any).Microsoft;
  // let locs = Microsoft.Maps.SpatialMath.getRegularPolygon(
  //   center,
  //   radius,
  //   edges,
  //   APP_CONFIG.MAPS.DB_GEOZONE_RADIUS_UNIT_FOR_MAPS()
  // );
  const google = (window as any).google;
  const locs = [];
  const lat = center.lat();
  const lng = center.lng();
  const d = radius / 1000; // convert to km
  const R = 6371; // Radius of the Earth in km
  const dLat = d / R; // dLat in radians
  const dLng = d / (R * Math.cos((Math.PI * lat) / 180)); // dLng in radians
  for (let i = 0; i < edges; i++) {
    const brng = (i * 2 * Math.PI) / edges; // Bearing in radians
    const lat2 = lat + (dLat * Math.sin(brng)) / (Math.PI / 180); // New latitude
    const lng2 = lng + (dLng * Math.cos(brng)) / (Math.PI / 180); // New longitude
    locs.push(new google.maps.LatLng(lat2, lng2));
  }
  // close the polygon
  if (isPolygon) {
    locs.push(locs[0]);
  }
  if (APP_CONFIG.DEBUG.GEOZONES)
    console.log(
      "[getCircleLocs]",
      locs.map((loc: any) => {
        return {
          lat: loc.lat(),
          lng: loc.lng(),
        };
      })
    );
  return isPolygon ? locs.slice(0, locs.length - 1) : locs;
};

// Create a contour line that represents a circle.
export function createCircle(
  center: google.maps.LatLng,
  radius: any,
  color: any
) {
  // const Microsoft = (window as any).Microsoft;
  // return new Microsoft.Maps.ContourLine(
  //   getCircleLocs(center, radius, 36 * 2),
  //   color
  // );
  const google = (window as any).google;
  return new google.maps.Circle({
    center: center,
    radius: radius,
    fillColor: color,
    fillOpacity: 0.2,
    strokeColor: color,
    strokeOpacity: 0.6,
    strokeWeight: 2,
  });
}

export const renderCircle = (
  mapRef: any,
  center: google.maps.LatLng,
  radius: number,
  colorRGB: [number, number, number],
  drawShapeOnly: boolean = false
) => {
  const parsedRadius = typeof radius === "string" ? parseFloat(radius) : radius;

  if (
    !parsedRadius ||
    isNaN(parsedRadius) ||
    parsedRadius <= 0 ||
    parsedRadius > 65000
  ) {
    console.error("[renderCircle] Invalid radius:", radius);
    return null;
  }

  if (APP_CONFIG.DEBUG.GEOZONES)
    console.log(
      "Rendering circle at",
      center.lat(),
      center.lng(),
      "with radius",
      radius
    );
  // if (!drawShapeOnly) mapRef.current.map.layers.clear();
  if (APP_CONFIG.DEBUG.GEOZONES)
    console.log("color", `rgba(${colorRGB.join(",")},0.2)`);
  const circleLayer = new google.maps.Circle({
    strokeColor: `rgba(${colorRGB.join(",")},0.6)`,
    strokeOpacity: 1,
    strokeWeight: 2,
    fillColor: `rgba(${colorRGB.join(",")},0.3)`,
    fillOpacity: 1,
    center: center,
    radius: parsedRadius,
  });
  mapRef.current.objects.mCircle = circleLayer;
  circleLayer.setMap(mapRef.current.map);
  if (drawShapeOnly) return circleLayer;

  // center the map to the circle
  // mapRef.current.map.setView({
  //   bounds: Microsoft.Maps.LocationRect.fromLocations(
  //     getCircleLocs(center, radius, 36 * 2)
  //   ),
  //   padding: 20,
  // });
  const bounds = circleLayer.getBounds();

  if (bounds) {
    mapRef.current.map.fitBounds(bounds, 20);
  } else {
    console.error(
      "[renderCircle] getBounds() returned null for radius:",
      parsedRadius
    );
  }
  // fit the map to the circle
  mapRef.current.map.fitBounds(bounds, 20);
  console.log("circleLayer", circleLayer);

  return circleLayer;
};

export const renderPolyline = (
  mapRef: any,
  center: any,
  edgePosition: any,
  colorRGB: [number, number, number],
  drawShapeOnly: boolean = false
) => {
  // Create a dashed polyline from the center to the radius marker
  const polyline = new google.maps.Polyline({
    path: [center, edgePosition],
    strokeColor: `rgba(${colorRGB.join(",")},0.6)`,
    strokeOpacity: 1.0,
    strokeWeight: 2,
    geodesic: true,
    icons: [
      {
        icon: {
          path: "M 0,-1 0,1",
          strokeOpacity: 1,
          scale: 4,
        },
        offset: "0",
        repeat: "10px",
      },
    ],
  });

  // Store the polyline in the map objects for future reference
  mapRef.current.objects.mRadiusLine = polyline;
};

export const renderPolygon = (
  mapRef: any,
  center: any,
  polygonPoints: any,
  colorRGB: [number, number, number],
  drawShapeOnly: boolean = false
) => {
  if (!polygonPoints || polygonPoints.length < 3) {
    console.error("Invalid polygon points, cannot render the polygon.");
    return;
  }
  const google = (window as any).google;
  if (APP_CONFIG.DEBUG.GEOZONES)
    console.log(
      "Rendering polygon at",
      center.lat(),
      center.lng(),
      "with points",
      polygonPoints
    );
  // if (!drawShapeOnly) mapRef.current.map.layers.clear();
  if (APP_CONFIG.DEBUG.GEOZONES)
    console.log("color", `rgba(${colorRGB.join(",")},0.2)`);
  const polygonLayer = new google.maps.Polygon({
    paths: polygonPoints,
    strokeColor: `rgba(${colorRGB.join(",")},0.6)`,
    strokeOpacity: 1,
    strokeWeight: 2,
    fillColor: `rgba(${colorRGB.join(",")},0.3)`,
    fillOpacity: 1,
  });
  mapRef.current.objects.mPolygon = polygonLayer;
  // mapRef.current.objects.mPolygon = new Microsoft.Maps.ContourLine(
  //   polygonPoints,
  //   `rgba(${colorRGB.join(",")},0.2)`
  // ); // createCircle(center, radius, `rgba(${colorRGB.join(',')},0.2)`);
  // const polygonLayer = new Microsoft.Maps.ContourLayer(
  //   [mapRef.current.objects.mPolygon],
  //   {
  //     colorCallback: (val: any) => val,
  //     polygonOptions: {
  //       strokeThickness: 2,
  //       strokeColor: `rgba(${colorRGB.join(",")},0.6)`, // 'rgba(0,100,0,0.5)',
  //       visible: true,
  //     },
  //   }
  // );
  polygonLayer.setMap(mapRef.current.map);
  if (drawShapeOnly) return polygonLayer;

  // center the map to the polygon
  // mapRef.current.map.setView({
  //   bounds: Microsoft.Maps.LocationRect.fromLocations(polygonPoints),
  //   padding: 20,
  // });
  const bounds = new google.maps.LatLngBounds();
  polygonPoints.forEach((point: TLatLng) => {
    bounds.extend(point);
  });
  // fit the map to the polygon
  mapRef.current.map.fitBounds(bounds, 20);

  return polygonLayer;
};

export const renderRoute = (
  mapRef: any,
  center: google.maps.LatLng,
  routePoints: google.maps.LatLng[],
  colorRGB: [number, number, number],
  drawShapeOnly: boolean = false
) => {
  if (!routePoints || routePoints.length < 2) return;
  const Microsoft = (window as any).Microsoft;
  if (APP_CONFIG.DEBUG.GEOZONES)
    console.log(
      "Rendering route at",
      center.lat(),
      center.lng(),
      "with points",
      routePoints
    );

  // remove existing routes
  // if (mapRef.current.objects.mRoutes)
  //   mapRef.current.objects.mRoutes.forEach((route: any) =>
  //     mapRef.current.map.entities.remove(route)
  //   );
  // mapRef.current.objects.mRoutes = [];

  if (APP_CONFIG.DEBUG.GEOZONES)
    console.log("color", `rgba(${colorRGB.join(",")},0.2)`);
  // const line = new Microsoft.Maps.Polyline(routePoints, {
  //   strokeColor: `rgba(${colorRGB.join(",")},0.6)`,
  //   strokeThickness: 2,
  // });
  // mapRef.current.map.entities.push(line);
  const line = new google.maps.Polyline({
    path: routePoints,
    geodesic: true,
    strokeColor: "#FF0000",
    strokeOpacity: 1.0,
    strokeWeight: 2,
  });
  mapRef.current.objects.mRoutes.push(line);
  if (drawShapeOnly) return;

  // center the map to the route
  // mapRef.current.map.setView({
  //   bounds: Microsoft.Maps.LocationRect.fromLocations(routePoints),
  //   padding: 20,
  // });
  const bounds = new google.maps.LatLngBounds();
  routePoints.forEach((point) => {
    const lat = (
      typeof point.lat === "number" ? point.lat : point.lat()
    ) as number;
    const lng = (
      typeof point.lng === "number" ? point.lng : point.lng()
    ) as number;
    bounds.extend({ lat, lng });
  });
  mapRef.current.map?.fitBounds(bounds, 20);
};

export class CustomRenderer implements Renderer {
  dataPoints: TDataPoint[] | DeviceEvent[] | DeviceListingResponse[] = [];
  entity_label: string;

  constructor(
    dataPoints: TDataPoint[] | DeviceEvent[] | DeviceListingResponse[],
    entity_label: string
  ) {
    this.dataPoints = dataPoints;
    this.entity_label = entity_label;
  }

  render(cluster: Cluster, stats: ClusterStats, map: google.maps.Map): Marker {
    return customClusterRenderer(
      cluster,
      stats,
      map,
      this.dataPoints,
      this.entity_label
    );
  }
}

export type ClusterVehiclesListItem = {
  deviceid?: string;
  device_id?: string;
  unit_name?: string;
  cid?: string;
  serial_number?: string;
  description?: string;
  displayname?: string;
  drivername?: string;
  ft_device_description?: string;
  ft_displayname?: string;
  eventdata?: {
    latitude?: number;
    longitude?: number;
  };
};

export const CLUSTER_VEHICLES_VIEW_MORE_EVENT =
  "map:cluster-vehicles-view-more";

export type ClusterVehiclesViewMoreEventDetail = {
  markers: ClusterVehiclesListItem[];
  count: number;
  entityLabel: string;
};

function attachClusterInfoBoxListeners(
  infobox: google.maps.InfoWindow,
  markers: ClusterVehiclesListItem[],
  count: number,
  entity_label: string
) {
  google.maps.event.addListenerOnce(infobox, "domready", () => {
    document
      .querySelectorAll<HTMLButtonElement>(".device-btn")
      .forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          const index = parseInt(
            (e.currentTarget as HTMLElement).dataset.deviceIndex || "-1"
          );
          if (index >= 0 && markers[index]) {
            console.log("You clicked on the device:", markers[index]);
            const clickedDeviceLat = (markers[index] as any).eventdata
              ?.latitude;
            const clickedDeviceLong = (markers[index] as any).eventdata
              ?.longitude;

            const map = (window as any).map.map;
            if (map && map.setCenter && clickedDeviceLat && clickedDeviceLong) {
              map.setCenter({ lat: clickedDeviceLat, lng: clickedDeviceLong });
              map.setZoom(25);
            }
          }
        });
      });

    document
      .querySelectorAll<HTMLButtonElement>(".cluster-view-more-btn")
      .forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          window.dispatchEvent(
            new CustomEvent<ClusterVehiclesViewMoreEventDetail>(
              CLUSTER_VEHICLES_VIEW_MORE_EVENT,
              {
                detail: {
                  markers,
                  count,
                  entityLabel: entity_label,
                },
              }
            )
          );
          infobox.close();
        });
      });
  });
}

/**
 * Custom renderer for google map cluster
 */
export const customClusterRenderer = (
  cluster: Cluster, // { count, position }: { count: number; position: any },
  stats: ClusterStats,
  map: google.maps.Map,
  dataPoints: TDataPoint[] | DeviceEvent[] | DeviceListingResponse[],
  entity_label: string
): Marker => {
  const google = (window as any).google;

  // Define variables for minimum cluster radius, and how wide the outline area of the circle should be.
  const minRadius = 12;
  const outlineWidth = 7;

  // Calculate the radius of the cluster based on the number of pushpins in the cluster, using a logarithmic scale.
  const radius = (Math.log(cluster.count) / Math.log(10)) * 5 + minRadius;

  // Default cluster color is red.
  let fillColor = "rgba(255, 40, 40, 0.7)";

  if (cluster.count < 10) {
    // Make the cluster green if there are less than 10 pushpins in it.
    fillColor = "rgba(0, 130, 0, 0.7)";
  } else if (cluster.count < 100) {
    // Make the cluster orange if there are 10 to 99 pushpins in it.
    fillColor = "rgba(200, 100, 0, 0.7)";
  } else if (cluster.count < 250) {
    // Make the cluster red if there are 100 to 250 pushpins in it.
    fillColor = "rgba(200, 0, 0, 0.7)";
  }

  const parser = new DOMParser();
  // Create an SVG string of two circles, one on top of the other, with the specified radius and color.
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${radius * 2}" height="${radius * 2}">
      <circle cx="${radius}" cy="${radius}" r="${radius}" fill="${fillColor}" />
      <circle cx="${radius}" cy="${radius}" r="${radius - outlineWidth}" fill="${fillColor}" />
      <text x="50%" y="50%" style="fill:#fff; font-weight:bold;" text-anchor="middle" font-size="12" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${cluster.count}</text>
    </svg>
  `;
  const pinSvg = parser.parseFromString(
    svg.toString(),
    "image/svg+xml"
  ).documentElement;

  const clusterOptions = {
    map,
    position: cluster.position,
    content: pinSvg,
  };
  if (APP_CONFIG.DEBUG.MAP_MARKERS)
    console.log("MARKERS INIT CLUSTER MARKER ======");
  const clusterMarker = new google.maps.marker.AdvancedMarkerElement(
    clusterOptions
  );
  if (!!(dataPoints as DeviceEvent[])?.[0]?.status?.statuscode) {
    return clusterMarker; // if the dataPoints are DeviceEvent, return the cluster marker without infobox
  }
  // create a infobox
  const initialMarkers = dataPoints as ClusterVehiclesListItem[];
  const infobox = new google.maps.InfoWindow({
    content: clusterInfoboxContent(initialMarkers, cluster.count, entity_label),
    headerDisabled: true,
    maxWidth: 200,
    zIndex: APP_CONFIG.MAPS.INFOBOX_ZINDEX,
  });
  attachClusterInfoBoxListeners(
    infobox,
    initialMarkers,
    cluster.count,
    entity_label
  );

  // save all infoboxes to close them on map click
  (window as any).clusterInfoboxes = (window as any).clusterInfoboxes || [];
  (window as any).clusterInfoboxes.push({
    position: cluster.position,
    infobox,
  });
  // close infobox on map click
  google.maps.event.addListener(map, "click", function () {
    infobox.close();
  });
  //show infobox on hover
  clusterMarker.addEventListener("mouseover", () => {
    if (APP_CONFIG.DEBUG.MAP_MARKERS) console.log("MOUSEOVER");
    // toggle display of infobox content
    const clusterDeviceIDs = (cluster?.markers || []).map(
      (marker: any) => marker.title
    );
    const filteredDataPoints = (
      dataPoints as
        | TDataPoint[]
        | DeviceEvent[]
        | DeviceListingResponse[] as ClusterVehiclesListItem[]
    ).filter(
      (dataPoint) =>
        (!!dataPoint?.deviceid &&
          clusterDeviceIDs.includes(`${dataPoint?.deviceid}`)) ||
        (!!dataPoint?.device_id &&
          clusterDeviceIDs.includes(`${dataPoint?.device_id}`))
    );
    if (APP_CONFIG.DEBUG.MAP_MARKERS) {
      console.log("MARKERS dataPoints", dataPoints);
      console.log("CLUSTER MARKERS", cluster?.markers);
      console.log("MARKERS clusterDeviceIDs", clusterDeviceIDs);
      console.log("MARKERS filtered", filteredDataPoints);
    }
    const newContent = clusterInfoboxContent(
      filteredDataPoints,
      cluster.count,
      entity_label
    );
    infobox.setContent(newContent);
    attachClusterInfoBoxListeners(
      infobox,
      filteredDataPoints,
      cluster.count,
      entity_label
    );
    // open infobox
    infobox.open({
      anchor: clusterMarker,
      map: map,
    });
    // close any other infoboxes
    (window as any).clusterInfoboxes.forEach((infoboxObj: any) => {
      if (infoboxObj.position !== cluster.position) {
        infoboxObj.infobox.close();
      }
    });
  });
  //close on mouseout
  clusterMarker.addEventListener("mouseout", () => {
    // disabling as we don't want to close the infobox on mouseout
    // we need to allow user to scroll through the asset list
    // user can click anywhere on the map to close the infobox
    // infobox.close();
  });
  return clusterMarker;
};

function clusterInfoboxContent(
  markers: ClusterVehiclesListItem[],
  count: number,
  entity_label: string
) {
  let showDriver = false;
  try {
    const storedUser = localStorage.getItem("user");

    if (storedUser) {
      const userData = JSON.parse(storedUser);
      showDriver = userData?.account?.hasdrivers == 1;
    }
  } catch (e) {
    console.warn("Could not read driver permissions in map helper", e);
  }
  const description = [];

  // max-height of 180px allows display of first 5 vehicles and the view all button as well without the need to scroll
  description.push(
    `<div class="relative text-[12px] w-36 text-accent-blue-dark cursor-default" style="max-height:180px;overflow-y:auto;">` +
      `<h4 class="sticky text-[14px] font-bold top-0 bg-accent-blue-pale rounded-sm px-2 py-1">${count} ${entity_label}</h4>` +
      `<ul class="list-none p-0 m-0">`
  );

  if (APP_CONFIG.DEBUG.MAP_MARKERS) {
    console.log("MARKERS [clusterInfoboxContent]", markers);
  }

  const maxMarkersInHover = 5;
  const visibleMarkers = markers.slice(0, maxMarkersInHover);

  for (let i = 0; i < visibleMarkers.length; i++) {
    const marker = visibleMarkers[i];
    const markerDescription =
      marker.description || marker.ft_device_description || "";
    const markerDisplayName = marker.displayname || marker.ft_displayname || "";
    const label = marker?.serial_number
      ? `${marker.serial_number}${
          marker.deviceid || marker.device_id
            ? ` | ${marker.deviceid || marker.device_id}`
            : ""
        }`
      : `${consistentDisplayAssetItem(
          marker.deviceid,
          markerDescription,
          markerDisplayName,
          marker.drivername,
          showDriver
        )}`;

    description.push(
      `<li>
        <button 
          class="device-btn block w-full text-left px-2 py-1 whitespace-nowrap overflow-hidden text-ellipsis ${
            i > 0 ? " border-t border-t-heading-gray888/25" : ""
          }"
          title="${label}"
          data-device-index="${i}">
            ${label}
        </button>
      </li>`
    );
  }

  // in either cases (whether there are more than 5 markers or not), we show this button
  // the text of the button will be "View Details" if there are 5 or less markers, and
  // "View All (X)" if there are more than 5 markers, where X is the total number of markers in the cluster
  if (markers.length > maxMarkersInHover || true) {
    description.push(
      `<li class="border-t border-t-heading-gray888/25 p-2">
        <button
          class="cluster-view-more-btn w-full rounded-md border border-accent-blue-dark text-accent-blue-dark font-semibold px-2 py-1 hover:bg-accent-blue-dark hover:text-white"
          type="button">` +
        (markers.length > maxMarkersInHover
          ? `View All (${markers.length})`
          : `View Details`) +
        `</button>
      </li>`
    );
  }

  description.push(`</ul></div>`);
  return description.join("");
}

export const clearCorridor = (mapRef: any) => {
  // remove existing routes
  mapRef.current.objects.mCorridorElements.forEach(
    (corridorElement: any, i: number) => {
      corridorElement.setMap(null);
    }
  );
  mapRef.current.objects.mCorridorElements = [];
};

export const renderCorridor = (
  mapRef: any,
  routePoint: google.maps.LatLng,
  radius: number,
  colorRGB: ColorRGB,
  prevPoint?: google.maps.LatLng
) => {
  const google = (window as any).google;
  const strokeColor = `rgba(${colorRGB.join(",")},0.4)`;
  const fillColor = `rgba(${colorRGB.join(",")},0.2)`;
  // const circle = createCircle(routePoint, radius, fillColor);
  // const circleLayer = new Microsoft.Maps.ContourLayer([circle], {
  //   colorCallback: (val: any) => val,
  //   polygonOptions: {
  //     strokeThickness: 2,
  //     strokeColor,
  //     visible: true,
  //   },
  // });
  // mapRef.current.map.layers.insert(circleLayer);
  const circleLayer = new google.maps.Circle({
    strokeColor: `rgba(${colorRGB.join(",")},0.6)`,
    strokeOpacity: 1,
    strokeWeight: 2,
    fillColor: `rgba(${colorRGB.join(",")},0.3)`,
    fillOpacity: 1,
    center: routePoint,
    radius: radius,
  });
  circleLayer.setMap(mapRef.current.map);
  mapRef.current.objects.mCorridorElements.push(circleLayer);

  let rectLayer = null;
  let rect = null;
  // create rectangle using Microsoft.Maps.ContourLayer along path using this and the previous point
  if (!!prevPoint) {
    const prevPointLatLng = {
      latitude:
        typeof prevPoint?.lat === "number"
          ? prevPoint?.lat
          : prevPoint?.lat?.(),
      longitude:
        typeof prevPoint?.lng === "number"
          ? prevPoint?.lng
          : prevPoint?.lng?.(),
    } as TLatLng;
    const routePointLatLng = {
      latitude:
        typeof routePoint.lat === "number" ? routePoint.lat : routePoint.lat(),
      longitude:
        typeof routePoint.lng === "number" ? routePoint.lng : routePoint.lng(),
    } as TLatLng;
    const headingAngle = legacyGeoHeading(prevPointLatLng, routePointLatLng); // heading from A to B // ptA.lat, ptA.lon, ptB.lat, ptB.lon) - 90.0; // perpendicular
    const headingAnglePerpendicular = headingAngle - 90.0;
    var rp1 = legacyCalcRadiusPoint(
      prevPointLatLng,
      radius,
      headingAnglePerpendicular
    ); // JSMapPoint
    var rp2 = legacyCalcRadiusPoint(
      routePointLatLng,
      radius,
      headingAnglePerpendicular
    ); // JSMapPoint
    var rp3 = legacyCalcRadiusPoint(
      routePointLatLng,
      radius,
      headingAnglePerpendicular + 180.0
    ); // JSMapPoint
    var rp4 = legacyCalcRadiusPoint(
      prevPointLatLng,
      radius,
      headingAnglePerpendicular + 180.0
    ); // JSMapPoint
    var rectPts = [rp1, rp2, rp3, rp4];
    // const contourLine = new Microsoft.Maps.ContourLine(rectPts, fillColor); // 'rgba(0,100,0,0.2)');

    // create Microsoft.Maps.ContourLayer polygon using rectPts
    // rectLayer = new Microsoft.Maps.ContourLayer([contourLine], {
    //   colorCallback: (val: any) => val,
    //   polygonOptions: {
    //     strokeThickness: 2,
    //     strokeColor,
    //     visible: true,
    //   },
    // });
    // mapRef.current.map.layers.insert(rectLayer);
    const polygonLayer = new google.maps.Polygon({
      paths: rectPts,
      strokeColor: `rgba(${colorRGB.join(",")},0.6)`,
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: `rgba(${colorRGB.join(",")},0.3)`,
      fillOpacity: 1,
    });
    polygonLayer.setMap(mapRef.current.map);
    mapRef.current.objects.mCorridorElements.push(polygonLayer);
  }

  return {
    circle: circleLayer, // circle,
    circleLayer: circleLayer,
    rectLayer,
    rect,
  };
};

// -------------------------------------
// Geo operations - Legacy
// -------------------------------------

/* Square */
function geoSQ(val: number) {
  return val * val;
}

/* degrees to radians */
function geoRadians(deg: number) {
  return deg * (Math.PI / 180.0);
}

/* radians to degrees */
function geoDegrees(rad: number) {
  return rad * (180.0 / Math.PI);
}

function geoDistanceRadians(point1: TLatLng, point2: TLatLng) {
  var rlat1 = geoRadians(point1.latitude);
  var rlon1 = geoRadians(point1.longitude);
  var rlat2 = geoRadians(point2.latitude);
  var rlon2 = geoRadians(point2.longitude);
  var dtlat = rlat2 - rlat1;
  var dtlon = rlon2 - rlon1;
  var a =
    geoSQ(Math.sin(dtlat / 2.0)) +
    Math.cos(rlat1) * Math.cos(rlat2) * geoSQ(Math.sin(dtlon / 2.0));
  var rad = 2.0 * Math.atan2(Math.sqrt(a), Math.sqrt(1.0 - a));
  return rad;
}

export function legacyGeoHeading(point1: TLatLng, point2: TLatLng) {
  var rlat1 = geoRadians(point1.latitude);
  var rlon1 = geoRadians(point1.longitude);
  var rlat2 = geoRadians(point2.latitude);
  var rlon2 = geoRadians(point2.longitude);
  var rDist = geoDistanceRadians(point1, point2);
  var rad = Math.acos(
    (Math.sin(rlat2) - Math.sin(rlat1) * Math.cos(rDist)) /
      (Math.sin(rDist) * Math.cos(rlat1))
  );
  if (Math.sin(rlon2 - rlon1) < 0) {
    rad = 2.0 * Math.PI - rad;
  }
  var deg = geoDegrees(rad);
  return deg;
}

const EARTH_RADIUS_KM = 6371.0088;
const EARTH_RADIUS_METERS = EARTH_RADIUS_KM * 1000.0;

export function legacyGeoRadiusPoint(
  lat: number,
  lon: number,
  heading: number,
  radiusM: number
) {
  while (heading < 0.0) {
    heading += 360.0;
  }
  while (heading >= 360.0) {
    heading -= 360.0;
  }
  var crLat = geoRadians(lat); // radians
  var crLon = geoRadians(lon); // radians
  var d = radiusM / EARTH_RADIUS_METERS;
  var xrad = geoRadians(heading); // radians
  var rrLat = Math.asin(
    Math.sin(crLat) * Math.cos(d) +
      Math.cos(crLat) * Math.sin(d) * Math.cos(xrad)
  );
  var rrLon =
    crLon +
    Math.atan2(
      Math.sin(xrad) * Math.sin(d) * Math.cos(crLat),
      Math.cos(d) - Math.sin(crLat) * Math.sin(rrLat)
    );
  return {
    latitude: geoDegrees(rrLat),
    longitude: geoDegrees(rrLon),
  } as TLatLng; // same as JSMapPoint
}

export const legacyCalcRadiusPoint = (
  center: TLatLng,
  radiusM: number,
  heading: number
) => {
  const google = (window as any).google;
  const pt = legacyGeoRadiusPoint(
    center.latitude,
    center.longitude,
    heading,
    radiusM
  ); // { lat: <>, lon: <> }
  return new google.maps.LatLng(
    pt.latitude,
    pt.longitude
  ) as google.maps.LatLng;
};

// source: https://stackoverflow.com/q/18883601/1268350
export const calcDistanceInKmBetweenTwoPoints = (
  point1: TLatLng,
  point2: TLatLng
) => {
  const deg2rad = (deg: number) => {
    return deg * (Math.PI / 180);
  };

  var dLat = deg2rad(point2.latitude - point1.latitude);
  var dLon = deg2rad(point2.longitude - point1.longitude);
  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(point1.latitude)) *
      Math.cos(deg2rad(point2.latitude)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  var d = EARTH_RADIUS_KM * c; // Distance in km
  return d;
};

export const roundOffLatLong = (LatLng: any) => {
  return LatLng.toFixed(5).replace(".00000", "");
};
